import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Swords, Crown, ChevronRight, Sparkles } from "lucide-react";

import { RankBadge, TIER_META, TIER_ORDER } from "../components/RankBadge";
import { useAuth } from "../context/AuthContext";

function TierRow({ tier, idx, total }) {
  const t = TIER_META[tier];
  const isLast = idx === total - 1;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ delay: idx * 0.04, duration: 0.4 }}
      data-testid={`ranks-tier-row-${tier}`}
      className="relative grid grid-cols-[auto,1fr,auto] items-center gap-6 rounded-2xl border border-white/10 bg-[var(--surface)]/70 p-5 sm:p-6 backdrop-blur"
      style={{
        boxShadow: idx >= 5
          ? `inset 0 0 0 1px ${t.primary}30, 0 0 24px -6px ${t.primary}40`
          : `inset 0 0 0 1px ${t.primary}1f`,
      }}
    >
      {/* Big badge */}
      <div className="shrink-0">
        <RankBadge tier={tier} size="xl" animated showLabel={false} />
      </div>

      {/* Mid column — label, MMR band, flavor */}
      <div className="min-w-0">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="text-2xl sm:text-3xl font-black uppercase tracking-wide"
              style={{ color: t.primary, textShadow: idx >= 5 ? `0 0 18px ${t.primary}66` : undefined }}>
            {t.label}
          </h3>
          <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-400">
            {t.band[0]} – {t.band[1] >= 9000 ? "∞" : t.band[1]} MMR
          </span>
        </div>
        <p className="mt-2 text-base text-zinc-300 italic">"{t.flavor}"</p>
        <div className="mt-3 flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-500">
          <span>est. distribution</span>
          <div className="flex-1 max-w-[200px] h-1.5 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full" style={{
              width: `${Math.min(100, t.distribution * 3.5)}%`,
              background: `linear-gradient(90deg, ${t.secondary}, ${t.primary})`,
            }} />
          </div>
          <span className="text-zinc-300 tabular-nums font-bold">{t.distribution}%</span>
        </div>
      </div>

      {/* Index pill */}
      <div className="hidden sm:flex flex-col items-center gap-1 text-zinc-500">
        <span className="text-[9px] uppercase tracking-widest">tier</span>
        <span className="text-2xl font-black tabular-nums" style={{ color: t.primary }}>
          {idx + 1}
        </span>
      </div>

      {!isLast && (
        <ChevronRight
          aria-hidden
          className="hidden md:block absolute -bottom-7 left-1/2 -translate-x-1/2 rotate-90 text-zinc-700"
        />
      )}
    </motion.div>
  );
}

export function RanksExplainer({ ctaForAuthed = true }) {
  const { user } = useAuth();
  const showSignup = !user && !ctaForAuthed ? false : !user;
  return (
    <>
      <section className="text-center pt-6 pb-12" data-testid="ranks-explainer-hero">
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.4em] text-[var(--accent)]">
          <Swords size={12} /> the ladder
        </motion.div>
        <motion.h2 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                   className="mt-4 text-4xl sm:text-5xl font-black tracking-tight">
          How ranks work
        </motion.h2>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }}
                  className="mt-4 text-zinc-400 max-w-2xl mx-auto">
          Climb the 8 tiers of FootGuesser. Higher you go, fewer who follow.
        </motion.p>
      </section>

      <section className="grid md:grid-cols-3 gap-4 mb-12" data-testid="ranks-explainer">
        {[
          { h: "2-4 player FFA matches", b: "Ranked matches are 2–4 player free-for-all games. Win to gain MMR, lose to drop. Tier is calculated per mode — Classic and Career Path are tracked separately." },
          { h: "Unlocks after 10 casuals",   b: "Play 10 casual matches first to unlock ranked queues. We use this to filter smurfs and give new players a feel for the game." },
          { h: "28-day seasons",             b: "Each season lasts 28 days. End-of-season rewards your peak tier with a permanent achievement, so even a one-game streak counts forever." },
        ].map((c, i) => (
          <div key={i} className="rounded-2xl border border-white/10 bg-[var(--surface)] p-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--accent)] font-bold">{c.h}</div>
            <p className="mt-2 text-sm text-zinc-300 leading-relaxed">{c.b}</p>
          </div>
        ))}
      </section>

      <section className="space-y-3" data-testid="ranks-ladder">
        {TIER_ORDER.map((tier, idx) => (
          <TierRow key={tier} tier={tier} idx={idx} total={TIER_ORDER.length} />
        ))}
      </section>

      <section className="mt-16 rounded-2xl border border-white/10 bg-[var(--surface)] p-6 sm:p-8 space-y-4"
               data-testid="ranks-mmr-explainer">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-[var(--accent)]" />
          <h2 className="text-xs uppercase tracking-[0.3em] text-zinc-400">how mmr is calculated</h2>
        </div>
        <p className="text-sm text-zinc-300 leading-relaxed">
          FootGuesser uses an <span className="text-white font-bold">N-player Elo</span> model: each player's
          placement (1st, 2nd, 3rd…) is compared to their expected placement based on everyone's MMR. Beat
          opponents rated higher than you, and you gain more. Lose to someone you should have beaten, and the
          drop is bigger.
        </p>
        <p className="text-sm text-zinc-300 leading-relaxed">
          New players carry a <span className="text-[var(--accent)] font-bold">K-factor of 40</span> for their
          first 10 ranked games. After that K drops to <span className="text-[var(--accent)] font-bold">24</span>
          {" "}below 1800 MMR, and tightens to <span className="text-[var(--accent)] font-bold">16</span> at
          Diamond+ where tiny margins decide who reaches Master.
        </p>
        <p className="text-sm text-zinc-300 leading-relaxed">
          MMR is tracked <span className="text-white font-bold">per mode</span>. Climbing Classic doesn't lift
          your Career Path rank or vice versa — each ladder is its own grind.
        </p>
      </section>

      {showSignup && (
        <section className="mt-12 text-center">
          <Link to="/register" data-testid="ranks-cta-signup"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--accent)] text-black font-black uppercase tracking-widest hover:brightness-110 transition">
            Sign up to play →
          </Link>
        </section>
      )}
    </>
  );
}

export default function Ranks() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]" data-testid="ranks-page">
      <header className="px-6 py-5 max-w-6xl mx-auto flex items-center justify-between">
        <Link to="/" className="text-xs uppercase tracking-[0.4em] text-[var(--accent)] hover:brightness-125">
          ← home
        </Link>
        <nav className="flex items-center gap-1 text-xs uppercase tracking-[0.3em]">
          <Link to="/leaderboards" className="px-3 py-1.5 rounded-full hover:bg-white/5">Leaderboards</Link>
          {user && <Link to="/dashboard" className="px-3 py-1.5 rounded-full hover:bg-white/5">Dashboard</Link>}
        </nav>
      </header>

      <main className="max-w-6xl mx-auto px-6 pb-24">
        {/* HERO */}
        <section className="text-center pt-6 pb-12">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                      className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.4em] text-[var(--accent)]">
            <Swords size={12} /> the ladder
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                     className="mt-4 text-5xl sm:text-6xl font-black tracking-tight">
            The Ranked Ladder
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }}
                    className="mt-4 text-zinc-400 max-w-2xl mx-auto">
            Climb the 8 tiers of FootGuesser. Higher you go, fewer who follow.
          </motion.p>
        </section>

        {/* EXPLAINER */}
        <section className="grid md:grid-cols-3 gap-4 mb-12" data-testid="ranks-explainer">
          {[
            { h: "2-4 player FFA matches", b: "Ranked matches are 2–4 player free-for-all games. Win to gain MMR, lose to drop. Tier is calculated per mode — Classic and Career Path are tracked separately." },
            { h: "Unlocks after 10 casuals",   b: "Play 10 casual matches first to unlock ranked queues. We use this to filter smurfs and give new players a feel for the game." },
            { h: "28-day seasons",             b: "Each season lasts 28 days. End-of-season rewards your peak tier with a permanent achievement, so even a one-game streak counts forever." },
          ].map((c, i) => (
            <div key={i} className="rounded-2xl border border-white/10 bg-[var(--surface)] p-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--accent)] font-bold">{c.h}</div>
              <p className="mt-2 text-sm text-zinc-300 leading-relaxed">{c.b}</p>
            </div>
          ))}
        </section>

        {/* LADDER */}
        <section className="space-y-3" data-testid="ranks-ladder">
          {TIER_ORDER.map((tier, idx) => (
            <TierRow key={tier} tier={tier} idx={idx} total={TIER_ORDER.length} />
          ))}
        </section>

        {/* HOW MMR IS CALCULATED */}
        <section className="mt-16 rounded-2xl border border-white/10 bg-[var(--surface)] p-6 sm:p-8 space-y-4"
                 data-testid="ranks-mmr-explainer">
          <div className="flex items-center gap-2">
            <Sparkles size={14} className="text-[var(--accent)]" />
            <h2 className="text-xs uppercase tracking-[0.3em] text-zinc-400">how mmr is calculated</h2>
          </div>
          <p className="text-sm text-zinc-300 leading-relaxed">
            FootGuesser uses an <span className="text-white font-bold">N-player Elo</span> model: each player's
            placement (1st, 2nd, 3rd…) is compared to their expected placement based on everyone's MMR. Beat
            opponents rated higher than you, and you gain more. Lose to someone you should have beaten, and the
            drop is bigger.
          </p>
          <p className="text-sm text-zinc-300 leading-relaxed">
            New players carry a <span className="text-[var(--accent)] font-bold">K-factor of 40</span> for their
            first 10 ranked games — this means their MMR jumps faster while we figure out where they belong.
            After that, K drops to <span className="text-[var(--accent)] font-bold">24</span> below 1800 MMR, and
            tightens to <span className="text-[var(--accent)] font-bold">16</span> at Diamond+ where tiny margins
            decide who reaches Master.
          </p>
          <p className="text-sm text-zinc-300 leading-relaxed">
            MMR is tracked <span className="text-white font-bold">per mode</span>. Climbing Classic doesn't lift
            your Career Path rank or vice versa — each ladder is its own grind.
          </p>
        </section>

        {/* CTA */}
        <section className="mt-12 text-center">
          {user ? (
            <Link to="/ranked" data-testid="ranks-cta-play"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--accent)] text-black font-black uppercase tracking-widest hover:brightness-110 transition">
              <Crown size={16} /> Play Ranked →
            </Link>
          ) : (
            <Link to="/register" data-testid="ranks-cta-signup"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--accent)] text-black font-black uppercase tracking-widest hover:brightness-110 transition">
              Sign up to play →
            </Link>
          )}
        </section>
      </main>
    </div>
  );
}
